import { ChangeEvent, useRef, useState } from "react";
import { MergeWithOverwrite, ValidationErrorStyle } from "src/common";
import { Button, DocumentIcon, Flex, Grid, InputLabel, RemoveIcon, Text } from "src/components";

import styled from "@emotion/styled";
import { colors } from "src/theme";

const UploadWrapper = styled.div({
  position: 'relative',
  width: '100%',
});

const HiddenInput = styled.input({
  display: 'none',
});

const CustomUploadButton = styled.div<{ hasError: boolean, disabled?: boolean, isDragging?: boolean }>(
  props => ({
    boxSizing: 'border-box',
    width: '100%',
    padding: '16px',
    backgroundColor: colors.background7,
    borderRadius: '6px',
    border: `1px dashed ${props.hasError ? colors.error1 : colors.border5}`,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',

    '&:hover': {
      borderColor: props.disabled ? colors.border5 : colors.border4,
      backgroundColor: props.disabled ? 'transparent' : colors.background6
    },

    ...(props.disabled && {
      opacity: 0.7,
      color: colors.text2
    }),

    ...(props.isDragging && {
      borderColor: colors.primary,
      backgroundColor: colors.background6
    })
  })
);

const FlexStyle = styled(Flex)({
  width: '100%',
});

const ButtonStyle = styled(Button)({
  width: 'fit-content',
});

const IconWrapper = styled.div({
  padding: '10px',
  backgroundColor: '#F5F5F5',
  borderRadius: '50px',
  display: 'inline-flex',
})

export type FileType = { fileId?: string; fileName: string; fileSize: string; file?: File };

export type UploadProps = MergeWithOverwrite<React.InputHTMLAttributes<HTMLInputElement>, {
  label?: string;
  error?: string;
  className?: string;
  accept?: string;
  disabled?: boolean;
  multiple?: boolean;
  value?: Array<FileType>;
  onChange?: (files: FileType[]) => void;
  optional?: boolean;
}>;

export const Upload: React.FC<UploadProps> = ({
  label,
  error = '',
  value = [],
  accept = 'application/pdf,image/*',
  onChange,
  className,
  disabled,
  multiple = true,
  optional = false,
  ...props
}) => {
  const [files, setFiles] = useState<Array<FileType>>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (inputRef.current && !disabled) {
      inputRef.current.click();
    }
  };

  const addNewFiles = (newRawFiles: Array<File>) => {
    const newFiles = newRawFiles.map((file: File) => ({
      fileName: file.name,
      fileSize: `${Math.round(file.size / 1024)} KB`,
      file: file
    }));
    const updatedFiles = multiple ? [...files, ...newFiles] : newFiles;
    setFiles(updatedFiles);
    if (onChange) onChange(updatedFiles.length ? updatedFiles : []);
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newRawFiles = Array.from(e.target.files || []);
    addNewFiles(newRawFiles);
  }

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    if (onChange) onChange(updatedFiles.length ? updatedFiles : []);
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;

    const newRawFiles = Array.from(e.dataTransfer.files || []);
    addNewFiles(newRawFiles);
  };

  const renderFiles = () => {
    if (files.length === 0) return null;
    if (multiple) {
      return (
        <CustomUploadButton 
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          hasError={!!error}
          disabled={disabled}
          isDragging={isDragging}
        >
          <FlexStyle vertical gap={8}>
            {files.map((f, i) => 
              <Grid columnGap={8} columns="auto 1fr auto" key={i}>
                <DocumentIcon color={colors.icon2} width={24} height={24} />
                <Flex vertical gap={4}>
                  <Text type="small" bold color={colors.text2}>{f.fileName}</Text>
                  <Text type="tiny" color={colors.text4}>{f.fileSize}</Text>
                </Flex>
                <RemoveIcon onClick={() => {handleRemoveFile(i)}} />
              </Grid>
            )}
            <ButtonStyle btnType="textPrimary" size="small" onClick={handleClick}>Dodaj još</ButtonStyle>
          </FlexStyle>
        </CustomUploadButton>
      );
    }

    return (
      <CustomUploadButton
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        hasError={!!error}
        disabled={disabled}
        isDragging={isDragging}
      >
        <FlexStyle vertical gap={8}>
          <Grid columnGap={8} columns="auto 1fr auto" key={files[0].fileName}>
            <DocumentIcon color={colors.icon2} width={24} height={24} />
            <Flex vertical gap={4}>
              <Text type="small" bold color={colors.text2}>{files[0].fileName}</Text>
              <Text type="tiny" color={colors.text4}>{files[0].fileSize}</Text>
            </Flex>
            <RemoveIcon onClick={() => {handleRemoveFile(0)}} />
          </Grid>
          <ButtonStyle btnType="textPrimary" size="small" onClick={handleClick}>Zamijeni</ButtonStyle>
        </FlexStyle>
      </CustomUploadButton> 
    );
  };

  const renderUploadArea = () => 
    <CustomUploadButton 
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      hasError={!!error}
      disabled={disabled}
      isDragging={isDragging}
    >
      <Flex vertical align="center" gap={12}>
        <IconWrapper><DocumentIcon width={24} height={24}/></IconWrapper>
          <Flex gap={4}>
            <Text type="base" bold color={colors.primary}>
              Učitaj
            </Text>
            <Text type="base" bold color={colors.text2}>
              dokument{multiple ? 'e' : ''}
            </Text>
          </Flex>
      </Flex>
    </CustomUploadButton>

  return (
    <UploadWrapper className={className}>
      {label ? <InputLabel htmlFor={props.id} disabled={disabled} optional={optional}>{label}</InputLabel> : null}
      
      <HiddenInput
        ref={inputRef}
        type="file"
        multiple={multiple}
        {...props}
        disabled={disabled}
        id={props.id}
        onChange={handleChange}
        accept={accept}
        value={undefined}
      />
      {files.length ? 
        // ako ima učitanih dokumenata
        renderFiles()
        :
        renderUploadArea()
      }

      {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
    </UploadWrapper>
  );
};
